import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import SpaceModal from './SpaceModal';

const Dashboard = () => {
    const { user, logout, toggleTheme } = useContext(AuthContext);
    const [spacesByGroup, setSpacesByGroup] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedSpace, setSelectedSpace] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');

    const [organizations, setOrganizations] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState('');
    const [allLots, setAllLots] = useState([]);

    const fetchDashboardData = async (orgId) => {
        try {
            setLoading(true);
            const passedOrgId = (orgId && typeof orgId !== 'object') ? orgId : null;
            let activeOrgId = passedOrgId || selectedOrgId || user.organization_id;
            
            // If admin, fetch orgs
            if (user.role === 'admin' && organizations.length === 0) {
                const orgRes = await axios.get('/organizations');
                setOrganizations(orgRes.data);
                if (!activeOrgId && orgRes.data.length > 0) {
                    activeOrgId = orgRes.data[0].id;
                    setSelectedOrgId(activeOrgId);
                }
            }

            // Fetch lots
            const lotsRes = await axios.get('/lots');
            setAllLots(lotsRes.data);
            
            // Find lot for active org
            const targetLot = lotsRes.data.find(l => l.organization_id === activeOrgId) || lotsRes.data[0];
            
            if (targetLot) {
                const response = await axios.get(`/lots/${targetLot.id}/spaces`);
                setSpacesByGroup(response.data);
            } else {
                setSpacesByGroup({});
            }
        } catch (err) {
            console.error("Error fetching dashboard data", err);
            setError("Failed to load parking spaces.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { 
        fetchDashboardData(selectedOrgId); 
    }, []);

    const handleOrgChange = (e) => {
        const newOrgId = parseInt(e.target.value);
        setSelectedOrgId(newOrgId);
        fetchDashboardData(newOrgId);
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'GREEN': return 'space-green';
            case 'RED': return 'space-red';
            case 'YELLOW': return 'space-yellow';
            case 'OUT_OF_SERVICE': return 'space-out';
            default: return '';
        }
    };

    const filteredGroups = Object.keys(spacesByGroup).reduce((acc, groupName) => {
        const filteredSpaces = spacesByGroup[groupName].filter(space => {
            // Check status filter first
            if (statusFilter === 'AVAILABLE' && space.status !== 'GREEN') return false;
            if (statusFilter === 'OCCUPIED' && (space.status !== 'RED' && space.status !== 'YELLOW')) return false;
            if (statusFilter === 'OUT_OF_SERVICE' && space.status !== 'OUT_OF_SERVICE') return false;

            // Then check text search
            if (!searchQuery) return true;
            const query = searchQuery.toLowerCase();
            return space.space_number.toLowerCase().includes(query) || 
                   (space.vehicle && space.vehicle.truck_number.toLowerCase().includes(query));
        });
        if (filteredSpaces.length > 0) acc[groupName] = filteredSpaces;
        return acc;
    }, {});

    const activeLot = allLots.find(l => l.organization_id === (selectedOrgId || user.organization_id)) || allLots[0];

    if (loading && Object.keys(spacesByGroup).length === 0) return <div className="container">Loading Dashboard...</div>;
    if (error) return <div className="container" style={{ color: 'var(--danger)' }}>{error}</div>;

    return (
        <div className="container">
            <div className="header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <img src="/logo-primary.png" alt="Wagoing Logo" style={{ height: '50px' }} />
                    <h1 style={{ margin: 0 }}>
                        {user.role === 'admin' && organizations.length > 0 ? (
                            <select 
                                value={selectedOrgId} 
                                onChange={handleOrgChange}
                                style={{ fontSize: '24px', fontWeight: 'bold', padding: '5px 10px', backgroundColor: 'transparent', color: 'var(--text-color)', border: '1px solid var(--input-border)', borderRadius: '5px' }}
                            >
                                {organizations.map(org => (
                                    <option key={org.id} value={org.id} style={{ color: 'initial' }}>{org.name}</option>
                                ))}
                            </select>
                        ) : (
                            user?.organization_name || 'System Overview'
                        )}
                    </h1>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    
                    {/* Theme Toggle Switch */}
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '8px' }}>
                        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Dark Mode</span>
                        <input 
                            type="checkbox" 
                            checked={user.dark_mode} 
                            onChange={(e) => toggleTheme(e.target.checked)} 
                            style={{ width: '18px', height: '18px' }}
                        />
                    </label>

                    <div style={{ borderLeft: '1px solid var(--input-border)', paddingLeft: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <span>Logged in as: <strong>{user.email}</strong></span>
                        
                        {activeLot && !activeLot.is_paid_lot && (
                            <Link to="/valet" className="btn" style={{ backgroundColor: 'var(--success)', color: 'white', textDecoration: 'none' }}>Valet Check-In</Link>
                        )}
                        
                        {(user.role === 'admin' || user.role === 'org_admin') && (
                            <Link to="/admin" className="btn btn-primary" style={{ textDecoration: 'none' }}>Admin Panel</Link>
                        )}
                        <button onClick={logout} className="btn btn-danger">Log Out</button>
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: '30px', display: 'flex', gap: '15px' }}>
                <input 
                    type="text" 
                    placeholder="Search by Space (e.g., A1) or Truck Number (e.g., 72)..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ flex: 1 }}
                />
                <select 
                    value={statusFilter} 
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ width: '250px' }}
                >
                    <option value="ALL">All Spaces</option>
                    <option value="AVAILABLE">Available (GREEN)</option>
                    <option value="OCCUPIED">Occupied (RED & YELLOW)</option>
                    <option value="OUT_OF_SERVICE">Out of Service</option>
                </select>
            </div>

            {Object.keys(filteredGroups).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-color)', opacity: 0.7, fontSize: '18px' }}>
                    No spaces or trucks match your search.
                </div>
            ) : (
                Object.keys(filteredGroups).sort().map((groupName) => (
                    <div key={groupName} className="glass-card" style={{ marginBottom: '30px', padding: '20px' }}>
                        <h2 style={{ borderBottom: '2px solid var(--card-border)', paddingBottom: '10px', marginTop: 0 }}>{groupName}</h2>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginTop: '20px' }}>
                            {filteredGroups[groupName].sort((a, b) => a.space_number.localeCompare(b.space_number, undefined, {numeric: true})).map((space) => {
                                const statusClass = getStatusClass(space.status);
                                return (
                                    <div 
                                        key={space.id} 
                                        onClick={() => setSelectedSpace(space)} 
                                        className={`glass-card ${statusClass}`}
                                        style={{
                                            width: '150px', height: '100px', padding: '15px',
                                            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div style={{ fontWeight: 'bold', fontSize: '18px' }}>{space.space_number}</div>
                                        {space.vehicle && (
                                            <div style={{ textAlign: 'center', fontSize: '24px', fontWeight: '900' }}>
                                                #{space.vehicle.truck_number}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))
            )}

            {selectedSpace && (
                <SpaceModal space={selectedSpace} onClose={() => setSelectedSpace(null)} onActionComplete={fetchDashboardData} />
            )}
        </div>
    );
};

export default Dashboard;